
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import EnhancedStabilityProvider from './EnhancedStabilityProvider';
import NetworkHealthMonitor from '@/components/system/NetworkHealthMonitor';
import { AuthProvider } from '@/hooks/useAuth';
import { ErrorSeverity, logError } from '@/utils/error/errorLogger';
import PostHogProvider from './PostHogProvider';
import PostHogEventTracker from '@/components/analytics/PostHogEventTracker';

interface AppProvidersProps {
  children: React.ReactNode;
}

// Cache the query client to prevent recreation on hot reloads
let cachedQueryClient: QueryClient | null = null;

// Module-level initialization tracking
let providersInitialized = false;

// AppProviders component wraps the entire application
export function AppProviders({ children }: AppProvidersProps) {
  // Use a ref to track if this instance has already created providers
  // This helps prevent multiple initializations in development hot reloading
  const providersInitializedRef = useRef(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Check if providers have been initialized elsewhere
  const globalInitialization = typeof window !== 'undefined' && (window as any).__STABILITY_INITIALIZED__;
  
  // Initialize on mount
  useEffect(() => {
    // Skip if already initialized
    if (providersInitializedRef.current || providersInitialized) {
      return;
    }
    
    // Log provider initialization status
    if (process.env.NODE_ENV === 'development') {
      console.log('AppProviders initializing. Global initialization status:', globalInitialization ? 'Already initialized elsewhere' : 'First initialization');
    }
    
    // Mark as initialized
    providersInitializedRef.current = true;
    providersInitialized = true;
    setIsInitialized(true);
    
    // Cleanup on unmount - only in development
    return () => {
      if (process.env.NODE_ENV !== 'production') {
        providersInitialized = false;
        providersInitializedRef.current = false;
      }
    };
  }, [globalInitialization]);
  
  // Use React's useMemo to prevent recreating the client on every render
  // Also handle cached client across hot reloads
  const queryClient = useMemo(() => {
    if (cachedQueryClient) {
      return cachedQueryClient;
    }
    
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 60000,
          gcTime: 300000,
          retry: (failureCount, error) => {
            // Log the error before deciding on retry
            logError(
              `Query error (attempt ${failureCount})`, 
              ErrorSeverity.WARNING,
              'QueryClient',
              { error }
            );
            
            // Don't retry on 4xx errors (client errors)
            if (
              error instanceof Error &&
              'status' in error &&
              typeof error.status === 'number' &&
              error.status >= 400 &&
              error.status < 500
            ) {
              return false;
            }
            
            // Only retry up to 2 times for other errors
            return failureCount < 2;
          },
          retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
        },
        mutations: {
          onError: (error: unknown) => {
            logError(
              error instanceof Error ? error : String(error),
              ErrorSeverity.ERROR,
              'QueryClient'
            );
          }
        }
      },
    });
    
    // Cache client for hot reloads
    cachedQueryClient = client;
    
    return client;
  }, []);

  return (
    <EnhancedStabilityProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <PostHogProvider>
              {children}
              <Toaster />
            </PostHogProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </EnhancedStabilityProvider>
  );
}

export default AppProviders;
